import { ENTRETIEN_INITIAL } from '../../donnees/entretien-initial.js';
import donneesAppel from './donnees/appel.v1.json' with { type: 'json' };
/**
 * Données versionnées du lot L52 : règles de la file du jour, réponses rapides de l'appel, entretien initial.
 * Libellés, types et filtres sont des données, jamais de la logique (CLAUDE.md, règles non négociables).
 * Toute évolution incrémente `VERSION_DONNEES_BRIGITTE`.
 */
export const VERSION_DONNEES_BRIGITTE = '2026-10-17.4';

export type Filtre = 'tout' | 'appels' | 'relances' | 'pieces' | 'notaire' | 'comptes_rendus';

export const FILTRES: readonly Filtre[] = ['tout', 'appels', 'relances', 'pieces', 'notaire', 'comptes_rendus'];

export const LIBELLES_FILTRES: Readonly<Record<Filtre, string>> = {
  tout: 'Tout',
  appels: 'Appels',
  relances: 'Relances à valider',
  pieces: 'Pièces à vérifier',
  notaire: 'Notaire',
  comptes_rendus: 'Comptes rendus',
};

export interface RegleFile {
  /** Filtre qui range la tâche ; « autre » : visible dans « Tout » seulement. */
  filtre: Filtre | 'autre';
  /** Seule action principale de la ligne (CA-BRI-01a). */
  action: string;
  badge: string | null;
  /** Statut métier affiché sur la ligne (référentiel STA-xx). */
  statut?: string;
  /** Adresse de l'écran qui traite la tâche (identique à celle de la coquille, vérifié par test). */
  destination: string;
  /** Vrai : la date d'échéance est affichée avec la tâche (#15). */
  afficherEcheance?: boolean;
}

/** Règle des types de tâche inconnus : jamais de tâche perdue, elle reste visible dans « Tout ». */
export const REGLE_PAR_DEFAUT: RegleFile = { filtre: 'autre', action: 'Ouvrir', badge: null, destination: '/dossier' };

export const REGLES_FILE: Readonly<Record<string, RegleFile>> = {
  appel: { filtre: 'appels', action: 'Ouvrir l’appel', badge: 'appel', destination: '/appel' },
  // Réunion du 16/10 #53 : tâche d'appel générée en fin de cadence de relance.
  appel_fin_cadence: { filtre: 'appels', action: 'Ouvrir l’appel', badge: 'appel', destination: '/appel' },
  valider_relance: { filtre: 'relances', action: 'Relire', badge: 'relance', destination: '/relances' },
  greffe: { filtre: 'relances', action: 'Envoyer', badge: 'greffe', destination: '/relances' },
  verifier_piece: { filtre: 'pieces', action: 'Vérifier', badge: 'pièces', destination: '/documents' },
  notaire: { filtre: 'notaire', action: 'Rattacher', badge: 'notaire', destination: '/documents' },
  // Réunion du 16/10 #31 : compte rendu à faire, statut STA-09.
  compte_rendu: {
    filtre: 'comptes_rendus',
    action: 'Rédiger',
    badge: 'compte rendu',
    statut: 'Compte rendu à faire',
    destination: '/boite',
  },
  // Réunion du 16/10 #15, RG-29 : le mandant a répondu « plus tard, avec échéance ».
  tache_mandant: { filtre: 'autre', action: 'Ouvrir', badge: 'mandant', afficherEcheance: true, destination: '/dossier' },
  decision: { filtre: 'autre', action: 'Ouvrir', badge: 'décision', destination: '/dossier' },
};

/** Réponses rapides de l'appel : une touche, aucune saisie clavier (CA-BRI-02a). */
export interface ChoixRapide {
  code: string;
  libelle: string;
}
export interface GroupeChoix {
  champ: string;
  titre: string;
  choix: readonly ChoixRapide[];
}

/** Réponses rapides de M02 : donnée versionnée et neutre, sans cas de démonstration (donnees/appel.v1.json, constat du 26/10). */
export const SITUATIONS_MATRIMONIALES: GroupeChoix = donneesAppel.situationMatrimoniale;

export const CE_QU_ELLE_DIT: GroupeChoix = donneesAppel.ceQuIlOuElleDit;

export const CHAMPS_TEXTE_APPEL: readonly { champ: string; libelle: string }[] = [
  { champ: 'adresse', libelle: 'Adresse' },
  { champ: 'profession', libelle: 'Profession' },
  // Texte libre de « Ce qu'il ou elle dit » (donnée versionnée).
  donneesAppel.texteLibre,
];

export interface QuestionEntretien {
  code: string;
  question: string;
  /** Réponses proposées ; absent : saisie libre. */
  options?: readonly ChoixRapide[];
}

/** Entretien initial (réunion du 16/10, #38) : posées sur M02. */
/** Questions de l'entretien initial : la donnée commune à M02 et D16 (unifiée le 26/10). */
export const QUESTIONS_ENTRETIEN_INITIAL: readonly QuestionEntretien[] = ENTRETIEN_INITIAL.questions;

export const REFUS_NUMERO = 'Refuse que la famille ait son numéro';

/** /appel ouvert sans personne (menu, lien direct) : aucun appel en cours, renvoi vers la file du jour (constat du 26/10). */
export const AUCUN_APPEL = {
  titre: 'Aucun appel en cours',
  texte: 'Ouvrez un appel depuis la file du jour : la fiche de la personne s’affichera ici. Cabinet MAAT',
  lien: 'Aller à la file du jour',
} as const;

/**
 * Compte rendu d'appel envoyé par la route directe `comptesRendus.creer` (défaut assistante-01, décision du team-lead du 26/10) :
 * titre « Appel du <date> » et une ligne « Libellé : valeur » par réponse recueillie, en libellés, jamais en codes.
 */
export const COMPTE_RENDU_APPEL = {
  kind: 'appel',
  titre: 'Appel du {date}',
  ligneNote: 'Note',
  /** Appel terminé sans rien recueillir ni refus : le compte rendu le dit, sans rien inventer. */
  aucuneInformation: 'Aucune information recueillie pendant l’appel.',
} as const;
export const MODELE_COMPTE_RENDU = 'E2';

/**
 * Refus de partage du numéro : un `ConsentRecord` du contrat (packages/contracts, entities/confidentialite.ts), enregistré par
 * la route `confidentialite.consentements.enregistrer` (audit `consentement.enregistrement` porté par la route). Jamais
 * une forme propre à M02, jamais par `sync.push`.
 */
export const CONSENTEMENT_REFUS_NUMERO = { purpose: 'partage_numero_famille', granted: false, channel: 'telephone' } as const;
export const ROUTE_CONSENTEMENT = 'confidentialite.consentements.enregistrer';

/**
 * Rubriques de l'entité de synchronisation `appel` (ajout seul, écriture équipe) : aucun champ de la saisie de M02 n'existe
 * dans `PersonPatchSchema` ni dans `PersonStaffViewSchema`, donc rien n'est écrit sur l'entité `personne`.
 * `dossier: true` : la rubrique est rattachée au dossier, sans `personId` (entretien initial, #38).
 * `coordonnee` : la valeur est une coordonnée recueillie ; elle est gardée avec sa visibilité (équipe) en attendant la
 * création du contact par le contrat (RFC-L52-003).
 */
export interface RubriqueAppel {
  rubrique: string;
  dossier?: boolean;
  coordonnee?: { type: string; visibility: 'equipe' };
}

export const RUBRIQUES_APPEL: Readonly<Record<string, RubriqueAppel>> = {
  situationMatrimoniale: { rubrique: 'situation_declaree' },
  positionAppel: { rubrique: 'position_appel' },
  proposAppel: { rubrique: 'propos_appel' },
  profession: { rubrique: 'profession_declaree' },
  adresse: { rubrique: 'coordonnee_recueillie', coordonnee: { type: 'address', visibility: 'equipe' } },
};
export const RUBRIQUE_ENTRETIEN_INITIAL: RubriqueAppel = { rubrique: 'entretien_initial', dossier: true };
export const PREFIXE_ENTRETIEN = 'entretien.';
export const RUBRIQUE_PAR_DEFAUT = 'saisie_appel';
export const RUBRIQUE_ISSUE = 'issue';

/**
 * Nom de synchronisation des entités que M02 journalise sous leur nom du contrat (`person`, `task`). Le serveur de
 * synchronisation (packages/sync, politique-conflits.json) ne connaît que `personne` et `tache` : la correspondance est faite
 * à la frontière du transport (`transportSync`), jamais en laissant partir le nom du contrat.
 */
export const TYPES_SYNC: Readonly<Record<string, string>> = { task: 'tache' };

/** Ce que Brigitte doit reprendre quand le serveur refuse une mutation, par type d'entité de synchronisation. */
export const LIBELLES_A_REPRENDRE: Readonly<Record<string, string>> = {
  appel: 'Réponses recueillies pendant l’appel',
  tache: 'Clôture de la tâche',
  consentement: 'Refus de coordonnées à la famille',
  compte_rendu: 'Compte rendu de l’appel',
  personne: 'Fiche de la personne (profession, situation)',
  coordonnee: 'Adresse recueillie',
};
export const LIBELLE_A_REPRENDRE_PAR_DEFAUT = 'Saisie de l’appel';

/**
 * Attente quand la file d'envoi est déjà en cours de rejeu hors de la session (événement « online », autre écran) :
 * M02 réessaie à cet intervalle, au plus ce nombre de fois, tant que le réseau est là. Au-delà, rien n'est perdu : les
 * saisies restent sur l'appareil, le bandeau le dit et « Appel terminé » n'est pas annoncé.
 */
export const ATTENTE_REJEU = { delaiMs: 100, essais: 300 } as const;

/** Délai sans frappe au bout duquel un champ texte de M02 entre dans le journal de l'appareil (PER-2-06). */
export const DELAI_FRAPPE_MS = 400;
